"use client";

import { Close, Search } from "@ellaandil/ui";
import { Slot } from "@trydig/ui";
import { type ComponentProps, Suspense, useMemo } from "react";
import type { ISbStoryData } from "storyblok";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import {
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerMain,
	DrawerRoot,
	DrawerViewport,
} from "@/components/ui/Drawer";
import { GeoStatus } from "@/components/ui/GeoStatus";
import { IconButton } from "@/components/ui/IconButton";
import { Typography } from "@/components/ui/Typography";
import { CartTrigger } from "@/features/cart/CartModal";
import { useGlobal } from "@/features/global/GlobalContext";
import { GeoSelectorTrigger } from "@/features/navigation/GeoSelector";
import { HeaderCart } from "@/features/navigation/Header/components/HeaderCart";
import { useHeader } from "@/features/navigation/Header/components/HeaderProvider";
import { MainMenuView } from "@/features/navigation/Header/components/MobileMenu/components/MainMenuView";
import { SubSectionView } from "@/features/navigation/Header/components/MobileMenu/components/SubSectionView";
import { useActiveSection } from "@/features/navigation/Header/components/MobileMenu/hooks";
import { useSearchModal } from "@/features/search/SearchProvider";
import type { ArticlePageStoryblok } from "@/generated/sb/sb-types";
import { useTranslations } from "@/i18n/client";
import { useModal } from "@/lib/hooks/useModal";

const UnsuspendedMobileMenu = () => {
	const { preview } = useGlobal();
	const { isOpen, setOpen } = useModal("mobileMenu");
	const { open: openSearch } = useSearchModal();
	const { activeSection, setActiveSection, reset } = useActiveSection();
	const t = useTranslations();
	const { content } = useHeader();

	const activeSectionData = useMemo(() => {
		if (!activeSection) return null;
		return content?.find(
			(section) => section.component === "megaMenu" && section.title === activeSection,
		);
	}, [content, activeSection]);

	return (
		<DrawerRoot open={isOpen} onOpenChange={setOpen}>
			<DrawerViewport
				side="left"
				className="block lg:hidden !max-w-[600px] bg-background-bg-sand-1 backdrop:bg-overlay-3 backdrop:backdrop-blur-[2.5px]"
			>
				<DrawerContent className="px-4 !gap-8">
					<DrawerHeader className="h-12 flex items-center justify-between border-b border-border-medium">
						<Breadcrumbs
							items={[
								{
									label: t("menu.header"),
									onClick: activeSection ? reset : undefined,
								},
								...(activeSection ? [{ label: activeSection }] : []),
							]}
						/>
						<IconButton
							icon={Close}
							label={t("Close mobile menu")}
							variant="ghost-primary"
							size="lg"
							className="size-9"
							onClick={() => setOpen(false)}
						/>
					</DrawerHeader>
					<DrawerMain className="flex flex-col gap-grid-component-spacing-2xs pb-8">
						{activeSectionData && activeSectionData.component === "megaMenu" ? (
							<SubSectionView
								sections={activeSectionData.sections}
								stories={
									activeSectionData.stories as ISbStoryData<ArticlePageStoryblok>[] | undefined
								}
								preview={preview}
							/>
						) : (
							<MainMenuView onSectionSelect={setActiveSection} preview={preview} />
						)}
					</DrawerMain>
					{activeSectionData && activeSectionData.component === "megaMenu" ? null : (
						<DrawerFooter className="flex flex-col pb-8">
							<button
								type="button"
								onClick={() => {
									setOpen(false);
									openSearch();
								}}
								className="flex gap-1 items-center p-1.5"
							>
								<Search className="size-6" />
								<Typography variant="nav-item">{t("menu.search")}</Typography>
							</button>
							<GeoSelectorTrigger asChild>
								<GeoStatus disableTextPadding className="p-1.5" />
							</GeoSelectorTrigger>
							<CartTrigger className="flex gap-1 items-center p-1.5">
								<HeaderCart
									className="!p-0 !min-w-0 !min-h-0"
									countClassName="bg-background-bg-sand-3 text-text-dark-text-1"
								/>
								<Typography variant="nav-item">{t("menu.cart")}</Typography>
							</CartTrigger>
						</DrawerFooter>
					)}
				</DrawerContent>
			</DrawerViewport>
		</DrawerRoot>
	);
};

export const MobileMenu = () => {
	return (
		<Suspense>
			<UnsuspendedMobileMenu />
		</Suspense>
	);
};

export interface MobileMenuTriggerProps extends Omit<ComponentProps<"button">, "onClick"> {
	asChild?: boolean;
}

const UnsuspendedMobileMenuTrigger = ({ asChild, children, ...props }: MobileMenuTriggerProps) => {
	const { toggle } = useModal("mobileMenu");
	const Component = asChild ? Slot : "button";

	return (
		<Component {...props} onClick={toggle}>
			{children}
		</Component>
	);
};

export const MobileMenuTrigger: typeof UnsuspendedMobileMenuTrigger = (props) => {
	return (
		<Suspense>
			<UnsuspendedMobileMenuTrigger {...props} />
		</Suspense>
	);
};
